import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useDuplicateFertilizerPack,
	useGetFertilizerPacks,
	useGetFertilizers,
	usePostPlanFertilization,
	type FertilizerPack,
} from '@practium/features/api';
import { useToast } from '@practium/ui';
import { APPLICATION_METHOD_LABELS, MONTH_NAMES_PT } from '../constants/maintenance.constants';
import { createPlanningSchema } from '../schemas/planning.schema';
import type { FertilizationPlanAdvice, MaintenanceSectorFertilization } from '../types';
import {
	formatSaltFractionPercent,
	fromApiPeriod,
	mapPlanningFertilizerDemand,
	mapPlanningNutritionBalance,
	mapSectorFertilizationsToPlanning,
	toApiPeriod,
	toApiSaltFraction,
	type PlanningSectorResult,
} from '../utils/planningTableMapper';
import type { FertilizerPackModalMode } from './useFertilizerPackForm';

export type PlanningDemandView = 'hectare' | 'sector';
export type PlanningPackModalMode = FertilizerPackModalMode;

export interface PlanningFormState {
	method: '' | 'irrigation' | 'broad';
	startMonth: string;
	endMonth: string;
	vbox: string;
	cemMax: string;
	injectorFlow: string;
	packId: string;
	saltFraction: string;
	desiredEventsPerMonth: string;
}

type PlanningSnapshot = {
	form: PlanningFormState;
	sectors: PlanningSectorResult[];
};

const EMPTY_FORM: PlanningFormState = {
	method: '',
	startMonth: '',
	endMonth: '',
	vbox: '',
	cemMax: '',
	injectorFlow: '',
	packId: '',
	saltFraction: '',
	desiredEventsPerMonth: '',
};

const firstIrrigation = (recommendations: PlanningSectorResult['recommendations']) => {
	const list = Array.isArray(recommendations)
		? recommendations
		: recommendations
			? [recommendations]
			: [];
	return list[0]?.irrigation;
};

const generatedAtTime = (item: MaintenanceSectorFertilization) =>
	item.generatedAt ? new Date(item.generatedAt).getTime() : 0;

const latestMethod = (items: MaintenanceSectorFertilization[]) =>
	items.reduce((latest, item) => (generatedAtTime(item) > generatedAtTime(latest) ? item : latest), items[0])
		.method;

const hydrateFromAdvice = (advice: FertilizationPlanAdvice): PlanningSnapshot | null => {
	if (!advice.sectorFertilizations?.length) return null;
	const method = latestMethod(advice.sectorFertilizations);
	const currentEntries = advice.sectorFertilizations.filter((item) => item.method === method);
	const sectors = mapSectorFertilizationsToPlanning(currentEntries);
	const first = currentEntries[0];
	const periods = (first.periods ?? []).map(fromApiPeriod).filter(Boolean);
	const irrigation = firstIrrigation(first.recommendations);
	return {
		sectors,
		form: {
			method: method ?? '',
			startMonth: periods[0] ?? '',
			endMonth: periods[periods.length - 1] ?? periods[0] ?? '',
			vbox: irrigation?.vbox != null ? String(irrigation.vbox) : '',
			cemMax: irrigation?.cemMax != null ? String(irrigation.cemMax) : '',
			injectorFlow: irrigation?.injectorFlow != null ? String(irrigation.injectorFlow) : '',
			packId: advice.packId ?? '',
			saltFraction: formatSaltFractionPercent(irrigation?.saltFraction),
			desiredEventsPerMonth:
				irrigation?.desiredEvents != null ? String(irrigation.desiredEvents) : '',
		},
	};
};

interface UsePlanningFormOptions {
	advice: FertilizationPlanAdvice;
}

export const usePlanningForm = ({ advice }: UsePlanningFormOptions) => {
	const toast = useToast();
	const adviceId = advice._id ?? '';
	const hydrated = useMemo(() => hydrateFromAdvice(advice), [advice]);
	const [form, setForm] = useState<PlanningFormState>(() => hydrated?.form ?? EMPTY_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [sectors, setSectors] = useState<PlanningSectorResult[]>(() => hydrated?.sectors ?? []);
	const [persisted, setPersisted] = useState<PlanningSnapshot | null>(() => hydrated);
	const [isView, setIsView] = useState(() => (hydrated?.sectors.length ?? 0) > 0);
	const [demandView, setDemandView] = useState<PlanningDemandView>('hectare');
	const [packModal, setPackModal] = useState<{
		open: boolean;
		mode: PlanningPackModalMode;
		pack: FertilizerPack | null;
	}>({ open: false, mode: 'add', pack: null });

	const { data: packs = [] } = useGetFertilizerPacks();
	const { data: fertilizers = [] } = useGetFertilizers({ limit: 300 });
	const { mutateAsync: calculatePlan, isPending: isCalculating } = usePostPlanFertilization();
	const { mutateAsync: duplicatePack, isPending: isDuplicatingPack } = useDuplicateFertilizerPack();

	useEffect(() => {
		const next = hydrateFromAdvice(advice);
		setForm(next?.form ?? EMPTY_FORM);
		setErrors({});
		setSectors(next?.sectors ?? []);
		setPersisted(next);
		setIsView((next?.sectors.length ?? 0) > 0);
		setDemandView('hectare');
	}, [adviceId]);

	const showIrrigationFields = Boolean(form.method && form.startMonth && form.endMonth && form.method === 'irrigation');
	const hasPersisted = (persisted?.sectors.length ?? 0) > 0;
	const hasCalculated = sectors.length > 0;

	const planMonths = useMemo(
		() => (advice.periods ?? []).map(fromApiPeriod).filter(Boolean),
		[advice.periods]
	);
	const planningSchema = useMemo(() => createPlanningSchema(planMonths), [planMonths]);
	const selectedPack = packs.find((pack) => String(pack._id) === String(form.packId)) ?? null;
	const packOptions = packs.map((pack) => ({ value: String(pack._id), label: pack.name }));

	const set = useCallback((key: keyof PlanningFormState, value: string) => {
		setForm((current) => ({ ...current, [key]: value }));
		setErrors((current) => ({ ...current, [key]: '' }));
		if (key === 'method' || key === 'startMonth' || key === 'endMonth') {
			setSectors([]);
		}
	}, []);

	const fertilizerDemand = useMemo(
		() => mapPlanningFertilizerDemand(sectors, demandView === 'hectare', fertilizers),
		[demandView, fertilizers, sectors]
	);
	const nutritionDemand = useMemo(() => mapPlanningNutritionBalance(sectors), [sectors]);

	const handleCalculate = async () => {
		if (!adviceId) return;
		const parsed = planningSchema.safeParse(form);
		if (!parsed.success) {
			const fieldErrors: Record<string, string> = {};
			parsed.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			return;
		}

		try {
			const payload = {
				planId: adviceId,
				method: parsed.data.method,
				period: [toApiPeriod(parsed.data.startMonth), toApiPeriod(parsed.data.endMonth)] as [
					string,
					string,
				],
				...(parsed.data.method === 'irrigation'
					? {
							packId: parsed.data.packId,
							vbox: Number(parsed.data.vbox),
							cemMax: Number(parsed.data.cemMax),
							injectorFlow: Number(parsed.data.injectorFlow),
							saltFraction: toApiSaltFraction(parsed.data.saltFraction),
							...(parsed.data.desiredEventsPerMonth
								? { desiredEventsPerMonth: Number(parsed.data.desiredEventsPerMonth) }
								: {}),
						}
					: {}),
			};
			const result = (await calculatePlan(payload)) as PlanningSectorResult[];
			const snapshot: PlanningSnapshot = { form, sectors: result };
			setSectors(result);
			setPersisted(snapshot);
			setIsView(true);
			setErrors({});
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Erro ao calcular o planejamento.');
		}
	};

	const handleCancel = () => {
		if (!persisted) return;
		setForm(persisted.form);
		setSectors(persisted.sectors);
		setErrors({});
		setIsView(true);
		setDemandView('hectare');
	};

	const handleEdit = () => setIsView(false);

	const openPackModal = (mode: PlanningPackModalMode, pack?: FertilizerPack | null) => {
		setPackModal({ open: true, mode, pack: pack ?? null });
	};

	const closePackModal = () => setPackModal((current) => ({ ...current, open: false }));

	const handlePackSaved = (pack: FertilizerPack) => {
		setForm((current) => ({ ...current, packId: pack._id }));
		setErrors((current) => ({ ...current, packId: '' }));
	};

	const handleDuplicatePack = async () => {
		if (!form.packId) return;
		try {
			await duplicatePack(form.packId);
			toast.success('Pacote duplicado com sucesso.');
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Erro ao duplicar o pacote.');
		}
	};

	const handleClearPack = () => {
		setForm((current) => ({ ...current, packId: '' }));
		setErrors((current) => ({ ...current, packId: '' }));
	};

	const methodLabel = form.method ? APPLICATION_METHOD_LABELS[form.method] : '—';
	const startMonthLabel = form.startMonth
		? (MONTH_NAMES_PT[form.startMonth.split('-')[1]] ?? form.startMonth)
		: '—';
	const endMonthLabel = form.endMonth
		? (MONTH_NAMES_PT[form.endMonth.split('-')[1]] ?? form.endMonth)
		: '—';
	const packLabel = selectedPack?.name || '—';

	return {
		form,
		errors,
		set,
		planMonths,
		isView,
		hasPersisted,
		hasCalculated,
		isCalculating,
		showIrrigationFields,
		demandView,
		setDemandView,
		fertilizerDemand,
		nutritionDemand,
		packOptions,
		selectedPack,
		packModal,
		openPackModal,
		closePackModal,
		handlePackSaved,
		handleDuplicatePack,
		handleClearPack,
		isDuplicatingPack,
		handleCalculate,
		handleCancel,
		handleEdit,
		methodLabel,
		startMonthLabel,
		endMonthLabel,
		packLabel,
	};
};
